import { Button, Image, Input, Tabs, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { MdOutlineWarningAmber } from 'react-icons/md'

import { userManager } from '@/Core/Managers/User'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { ViewRow } from '@/Ship/Components/Detail/ViewRow/ViewRow'
import { FormSection } from '@/Ship/Components/Form/FormSection'
import { ResolveDelayModal } from '@/Ship/Components/Modals/ResolveDelayModal'
import { StatusBadge } from '@/Ship/Components/UI/StatusBadge/StatusBadge'
import { TabLabel } from '@/Ship/Components/UI/TabLabel/TabLabel'
import { cx } from '@/Ship/Helpers/cssClass'
import { safeExternalUrl } from '@/Ship/Helpers/safeExternalUrl'
import { useNotification } from '@/Ship/Hooks/useNotification'

import { CompleteTaskModal } from '../../Components/Modals/CompleteTaskModal'
import { ConfirmExecutionModal } from '../../Components/Modals/ConfirmExecutionModal'
import { ReportDelayModal } from '../../Components/Modals/ReportDelayModal'
import { SendPushModal } from '../../Components/Modals/SendPushModal'
import { DelaysList } from '../../Components/Sections/DelaysList'
import { RatingsTable } from '../../Components/Sections/RatingsTable'
import { StatusHistorySection } from '../../Components/Sections/StatusHistorySection'
import { DELAY_SOURCE_MAP, delayStatusOption, type DelaySource } from '../../Contracts/DelayStatus'
import { shiftLabel } from '../../Helpers/shiftField'
import { getUnitLabel } from '../../Helpers/unitLabel'
import { AssignmentViewTemplateStore } from '../../Stores/AssignmentViewTemplateStore'
import { ASSIGNMENT_STATUS_OPTIONS } from '../assignmentCells'

import styles from './AssignmentViewTemplate.module.css'

const { Text } = Typography

const formatDateTime = (value: unknown): string | null => {
    if (typeof value !== 'string' || value === '') return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const personName = (person: any): string | null => person?.fullName || person?.shortName || null

/**
 * Картка задачі (перегляд). Зверху зведення (номер, статус, створено, заявка) і банер
 * активної затримки з дією «Вирішити»; далі секції рядків у дві колонки: виконавець,
 * маршрут і вантаж, заявка (або замовлення для задачі без заявки), коментар диспетчера;
 * знизу вкладки затримок, оцінок та історії. ID, дати й автори — ще й у «Службовій
 * інформації» (DetailShell), тож окремих рядків для них немає.
 */
export const AssignmentViewTemplate = observer(({ formContext, crudContext }: any) => {
    const [store] = useState(() => new AssignmentViewTemplateStore())
    const { showSuccess, showError } = useNotification()
    const v: any = formContext?.values || {}

    const delays: any[] = v.delays || []
    const driverRatings: any[] = v.driverRatings || []
    const assignmentUuid: string = v.uuid || ''
    const permissions: Record<string, boolean> = v._permissions || {}

    const activeDelay = delays.find((d: any) => d.status !== 'resolved' && d.status !== 'not_resolved')
    const vehicleNeed = v.executor?.vehicleNeed
    const request = vehicleNeed?.transportRequest
    const dispatcher = v.executor?.dispatcher

    // Редагувати коментар диспетчера може лише адмін, диспетчер або старший диспетчер.
    const canEditDispatcherComment = userManager.hasAnyRole([
        'ROLE_ADMIN',
        'ROLE_SUPER_ADMIN',
        'ROLE_DISPATCHER',
        'ROLE_SENIOR_DISPATCHER',
    ])

    const reload = () => crudContext?.actions?.reloadData?.()

    // Keep latest uuid available to the EventManager `changeStatus` handler
    // (subscribed once on mount, otherwise would capture a stale uuid).
    const uuidRef = useRef(assignmentUuid)
    uuidRef.current = assignmentUuid

    // Локальний стан коментаря належить одному запису: після переходу на інший — скинути
    useEffect(() => {
        store.setSavedComment(undefined)
        store.setCommentEdit(null)
    }, [assignmentUuid])

    useEffect(() => {
        return store.subscribeEvents(crudContext?.eventManager, {
            getAssignmentUuid: () => uuidRef.current,
            reload,
            notify: { success: showSuccess, error: showError },
        })
    }, [crudContext])

    const handleSaveComment = async () => {
        const ok = await store.saveComment(assignmentUuid, { success: showSuccess, error: showError })
        if (ok) reload()
    }

    const statusOption = ASSIGNMENT_STATUS_OPTIONS.find((option) => option.value === v.status)
    const created = formatDateTime(v.createdAt || v.createdDate)
    const requestNumber = request ? request.externalCode || `#${request.id}` : null
    const routeUrl = safeExternalUrl(v.routeUrl)
    const vehicleNumber = v.vehicle?.registrationNumber || v.vehicle?.licensePlate
    const vehicleModel = [v.vehicle?.brand, v.vehicle?.model].filter(Boolean).join(' ')
    const weightUnit = getUnitLabel(vehicleNeed?.unit)
    const comment: string = (store.savedComment !== undefined ? store.savedComment : v.dispatcherComment) || ''
    const delaySource = activeDelay ? DELAY_SOURCE_MAP[activeDelay.source as DelaySource]?.label : null
    const delayReceived = activeDelay ? formatDateTime(activeDelay.receivedAt) : null
    const delayMeta = [delayReceived, delaySource].filter(Boolean).join(' · ')

    return (
        <div className={cx(styles, 'root')}>
            <div className={cx(styles, 'summary')}>
                <div className={cx(styles, 'summaryMain')}>
                    <span className={cx(styles, 'summaryId')}>{v.id ? `Завдання #${v.id}` : 'Завдання'}</span>
                    <StatusBadge option={statusOption} fallbackLabel={v.status || '—'} />
                </div>
                <div className={cx(styles, 'summaryMeta')}>
                    {created && <span>Створено {created}</span>}
                    {requestNumber && <span>Заявка {requestNumber}</span>}
                </div>
            </div>

            {activeDelay && (
                <div className={cx(styles, 'delay')} role="status">
                    <MdOutlineWarningAmber className={cx(styles, 'delayIcon')} aria-hidden="true" />
                    <div className={cx(styles, 'delayBody')}>
                        <div className={cx(styles, 'delayHead')}>
                            <span className={cx(styles, 'delayTitle')}>Активна затримка виконання</span>
                            <StatusBadge
                                option={delayStatusOption(activeDelay.status)}
                                fallbackLabel={activeDelay.status}
                            />
                            {delayMeta && <span className={cx(styles, 'delayMeta')}>{delayMeta}</span>}
                        </div>
                        {activeDelay.driverComment && (
                            <p className={cx(styles, 'delayText')}>{activeDelay.driverComment}</p>
                        )}
                    </div>
                    {permissions['resolve_delay'] && (
                        <Button
                            size="small"
                            className={cx(styles, 'delayAction')}
                            onClick={() => store.openResolveFor(activeDelay)}
                        >
                            Вирішити
                        </Button>
                    )}
                </div>
            )}

            <FormSection title="Виконавець">
                <div className={cx(styles, 'grid')}>
                    <ViewRow label="Водій">{personName(v.driver)}</ViewRow>
                    <ViewRow label="Транспортний засіб">
                        {vehicleNumber || vehicleModel ? (
                            <>
                                {vehicleNumber || vehicleModel}
                                {vehicleNumber && vehicleModel && (
                                    <span className={cx(styles, 'muted')}> · {vehicleModel}</span>
                                )}
                            </>
                        ) : null}
                    </ViewRow>
                    <ViewRow label="Зміна">
                        {v.shift?.uuid ? (
                            // Ресурс за ключем реєстру, не імпортом: картка зміни сама показує задачі
                            <Typography.Link
                                onClick={() =>
                                    OpenResourceCardTask({
                                        resource: 'Shift.Shift',
                                        primaryFieldValue: v.shift.uuid,
                                        mode: 'view',
                                    })
                                }
                            >
                                {shiftLabel(v.shift)}
                            </Typography.Link>
                        ) : null}
                    </ViewRow>
                    <ViewRow label="Перевізник">{v.carrier?.title}</ViewRow>
                    <ViewRow label="Найманий ТЗ">{v.isHired ? 'Так' : 'Ні'}</ViewRow>
                    <ViewRow label="Відповідальний диспетчер">
                        {personName(dispatcher) || personName(request?.responsibleDispatcher)}
                    </ViewRow>
                    <ViewRow label="е-ТТН">{v.eWaybillNumber}</ViewRow>
                </div>
            </FormSection>

            <FormSection title="Маршрут і вантаж">
                <div className={cx(styles, 'grid')}>
                    <ViewRow label="Кластер">{v.cluster?.title}</ViewRow>
                    <ViewRow label="Регіон">{v.region?.title}</ViewRow>
                    <ViewRow label="Пункт навантаження" className={cx(styles, 'full')}>
                        {v.loadingPoint?.title || vehicleNeed?.loadingPoint?.title}
                    </ViewRow>
                    <ViewRow label="Пункт розвантаження" className={cx(styles, 'full')}>
                        {v.unloadingPoint?.title || vehicleNeed?.unloadingPoint?.title}
                    </ViewRow>
                    <ViewRow label="Типовий маршрут" className={cx(styles, 'full')}>
                        {v.typicalRoute?.title || <span className={cx(styles, 'muted')}>Не підібрано</span>}
                    </ViewRow>
                    <ViewRow label="Маршрут (посилання)" className={cx(styles, 'full')}>
                        {routeUrl ? (
                            <a
                                href={routeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Відкриється в новій вкладці"
                            >
                                {String(v.routeUrl).trim()}
                            </a>
                        ) : (
                            v.routeUrl
                        )}
                    </ViewRow>
                    <ViewRow label="Вантаж">{v.cargo?.title || vehicleNeed?.cargo?.title}</ViewRow>
                    <ViewRow label={weightUnit ? `Загальна маса, ${weightUnit}` : 'Загальна маса'}>
                        {v.totalWeightTonnes}
                    </ViewRow>
                    <ViewRow label="Кількість ходок">{v.tripsCount}</ViewRow>
                </div>
            </FormSection>

            <FormSection title={request ? 'Заявка' : 'Замовлення'}>
                <div className={cx(styles, 'grid')}>
                    {request && (
                        <>
                            <ViewRow label="ID заявки">{request.id}</ViewRow>
                            <ViewRow label="Номер заявки в ERP">{request.externalCode}</ViewRow>
                        </>
                    )}
                    <ViewRow label="Об'єкт будівництва">
                        {v.constructionObject?.title || request?.constructionObject?.title}
                    </ViewRow>
                    <ViewRow label="Об'єкт виробництва">
                        {v.productionObject?.title || request?.productionObject?.title}
                    </ViewRow>
                    <ViewRow label="Відповідальний за замовлення" className={cx(styles, 'full')}>
                        {v.responsibleCustomerName ||
                            personName(v.responsibleCustomer) ||
                            personName(request?.responsibleCustomer)}
                    </ViewRow>
                </div>
            </FormSection>

            <FormSection title="Коментар диспетчера">
                {store.commentEdit !== null ? (
                    <div className={cx(styles, 'commentEditor')}>
                        <Input.TextArea
                            rows={3}
                            value={store.commentEdit}
                            onChange={(e) => store.setCommentEdit(e.target.value)}
                            aria-label="Коментар диспетчера"
                            autoFocus
                        />
                        <div className={cx(styles, 'commentActions')}>
                            <Button
                                size="small"
                                type="primary"
                                loading={store.savingComment}
                                onClick={handleSaveComment}
                            >
                                Зберегти
                            </Button>
                            <Button
                                size="small"
                                disabled={store.savingComment}
                                onClick={() => store.setCommentEdit(null)}
                            >
                                Скасувати
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className={cx(styles, 'comment')}>
                        <p className={`${cx(styles, 'commentText')}${comment ? '' : ` ${cx(styles, 'commentEmpty')}`}`}>
                            {comment || 'Не вказано'}
                        </p>
                        {canEditDispatcherComment && (
                            <Button size="small" onClick={() => store.setCommentEdit(comment)}>
                                {comment ? 'Редагувати' : 'Додати'}
                            </Button>
                        )}
                    </div>
                )}
            </FormSection>

            <Tabs
                defaultActiveKey="delays"
                items={[
                    {
                        key: 'delays',
                        label: <TabLabel count={delays.length}>Затримки</TabLabel>,
                        children: (
                            <DelaysList
                                delays={delays}
                                canResolve={!!permissions['resolve_delay']}
                                onShowPhotos={(urls) => store.showPhotos(urls)}
                                onResolve={(delay) => store.openResolveFor(delay)}
                            />
                        ),
                    },
                    {
                        key: 'ratings',
                        label: <TabLabel count={driverRatings.length}>Оцінки водія</TabLabel>,
                        children: <RatingsTable ratings={driverRatings} />,
                    },
                    {
                        key: 'history',
                        label: 'Історія статусів',
                        children: assignmentUuid ? (
                            <StatusHistorySection assignmentUuid={assignmentUuid} />
                        ) : (
                            <Text type="secondary">Недоступно</Text>
                        ),
                    },
                ]}
            />

            {/* Modals */}
            <Image.PreviewGroup
                preview={{
                    visible: store.previewOpen,
                    onVisibleChange: (visible: boolean) => store.setPreviewOpen(visible),
                    current: 0,
                }}
                items={store.previewPhotos.map((url) => ({ src: url }))}
            />
            <ReportDelayModal
                assignmentUuid={assignmentUuid}
                open={store.reportDelayOpen}
                onClose={() => store.setReportDelayOpen(false)}
                onReported={reload}
            />
            <CompleteTaskModal
                assignmentUuid={assignmentUuid}
                assignment={v}
                open={store.completeTaskOpen}
                onClose={() => store.setCompleteTaskOpen(false)}
                onCompleted={reload}
            />
            <ResolveDelayModal
                delay={store.selectedDelay}
                open={store.resolveModalOpen}
                onClose={() => store.setResolveModalOpen(false)}
                onResolved={reload}
            />
            <SendPushModal
                assignmentUuid={assignmentUuid}
                open={store.pushModalOpen}
                onClose={() => store.setPushModalOpen(false)}
            />
            <ConfirmExecutionModal
                assignmentUuid={assignmentUuid}
                assignmentStatus={v.status || ''}
                open={store.confirmOpen}
                onClose={() => store.setConfirmOpen(false)}
                onConfirmed={reload}
            />
        </div>
    )
})
